// Choosing "Blur background" opens this first (QUEUE row 206; Adam,
// 2026-10-03: "blur background should show you a thing that expands, shows you
// your current and blurred, and lets you confirm before applying it"). Your
// camera as it is now sits beside the same camera blurred; Apply turns blur
// on, Cancel leaves everything as it was.
//
// The blurred half runs lib/blur.ts's own pipeline on the raw camera track
// for as long as the sheet is open. The call keeps publishing the plain
// camera until Apply, and the preview's processor is stopped (the camera
// track underneath is left alone) on Apply, Cancel, Escape, or unmount.

import { useEffect, useRef, useState, type JSX } from "react";
import { createBlur, type BlurHandle } from "../lib/blur";
import { VideoView } from "./VideoView";

interface BlurPreviewSheetProps {
  /** The camera as the call has it right now. */
  readonly stream: MediaStream | null;
  readonly cameraOn: boolean;
  /** Front camera: mirrored, like the self view. */
  readonly mirrored: boolean;
  readonly onApply: () => void;
  readonly onCancel: () => void;
}

type BlurState = "idle" | "loading" | "ready" | "failed";

export function BlurPreviewSheet({ stream, cameraOn, mirrored, onApply, onCancel }: BlurPreviewSheetProps): JSX.Element {
  const videoTrack = stream?.getVideoTracks()[0] ?? null;
  const live = videoTrack !== null && cameraOn;
  const [blur, setBlur] = useState<{ state: BlurState; handle: BlurHandle | null }>({ state: "idle", handle: null });
  const handleRef = useRef<BlurHandle | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const release = (): void => {
    handleRef.current?.stop();
    handleRef.current = null;
  };

  useEffect(() => {
    if (!live || !videoTrack) {
      setBlur({ state: "idle", handle: null });
      return;
    }
    let cancelled = false;
    setBlur({ state: "loading", handle: null });
    createBlur(videoTrack).then(
      (handle) => {
        if (cancelled) {
          handle.stop();
          return;
        }
        handleRef.current = handle;
        setBlur({ state: "ready", handle });
      },
      () => {
        if (!cancelled) setBlur({ state: "failed", handle: null });
      },
    );
    return () => {
      cancelled = true;
      release();
    };
  }, [live, videoTrack]);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const apply = (): void => {
    release();
    onApply();
  };

  // A camera that is off has nothing to preview, but the choice still holds
  // for when it comes back on, so Apply stays available as long as there is a
  // camera at all.
  const canApply = videoTrack !== null && (!live || blur.state === "ready");

  const emptyLine = !live
    ? "Your camera is off. Turn it on to see a preview."
    : blur.state === "failed"
      ? "Background blur is not available here."
      : "Preparing preview";

  return (
    <div
      role="presentation"
      className="blurprev__scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Blur background" tabIndex={-1} className="blurprev">
        <div className="blurprev__title">Blur background</div>
        <div className="blurprev__pair">
          <figure className="blurprev__item">
            <div className="blurprev__frame">
              {live ? <VideoView track={videoTrack} mirrored={mirrored} muted fit="cover" /> : <div className="blurprev__empty">{emptyLine}</div>}
            </div>
            <figcaption>Now</figcaption>
          </figure>
          <figure className="blurprev__item">
            <div className="blurprev__frame">
              {live && blur.handle ? (
                <VideoView track={blur.handle.track} mirrored={mirrored} muted fit="cover" />
              ) : (
                <div className="blurprev__empty" role={blur.state === "failed" ? "alert" : undefined}>
                  {live && blur.state !== "failed" ? <span className="blurprev__spinner" aria-hidden="true" /> : null}
                  {emptyLine}
                </div>
              )}
            </div>
            <figcaption>Blurred</figcaption>
          </figure>
        </div>
        <div className="blurprev__actions">
          <button type="button" className="blurprev__btn blurprev__btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="blurprev__btn blurprev__btn--primary" onClick={apply} disabled={!canApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
